import { useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const vert = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const beamFrag = `uniform vec3 uColor; uniform float uK; uniform float uTop; varying vec2 vUv;
void main(){
  float halfW = mix(0.48, uTop, vUv.y);
  float dx = abs(vUv.x - 0.5);
  float a = (1.0 - smoothstep(halfW * 0.35, halfW, dx)) * smoothstep(0.0, 0.3, vUv.y) * (0.45 + 0.55 * vUv.y);
  gl_FragColor = vec4(uColor, a * uK * 0.12);
}`;
const poolFrag = `uniform vec3 uColor; uniform float uK; uniform float uA; uniform vec2 uC; varying vec2 vUv;
void main(){
  float r = length((vUv - uC) * vec2(1.0, 1.2)) * 2.0;
  float a = 1.0 - smoothstep(0.0, 1.0, r);
  gl_FragColor = vec4(uColor, a * a * uK * uA);
}`;
const WARM = new THREE.Color("#FFE3B8");
const glowMat = (frag, extra) =>
  new THREE.ShaderMaterial({ vertexShader: vert, fragmentShader: frag, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, uniforms: { uColor: { value: WARM }, uK: { value: 0 }, ...extra } });

// A small warm picture light above a painting, with a soft beam and a pool on the wall.
export function GalleryLight({ w, h, level }) {
  const m = useMemo(
    () => ({
      beam: glowMat(beamFrag, { uTop: { value: 0.16 } }),
      wall: glowMat(poolFrag, { uA: { value: 0.3 }, uC: { value: new THREE.Vector2(0.5, 0.56) } }),
      body: new THREE.MeshBasicMaterial({ color: "#6A5442", toneMapped: false }),
      lens: new THREE.MeshBasicMaterial({ color: "#6B5A48", toneMapped: false }),
    }),
    [],
  );
  const dark = useMemo(() => new THREE.Color("#5E4F40"), []);
  useFrame(() => {
    const k = level.current;
    m.beam.uniforms.uK.value = k;
    m.wall.uniforms.uK.value = k;
    m.lens.color.copy(dark).lerp(WARM, k);
  });
  const top = h / 2 + 0.3;
  return (
    <group>
      <mesh position={[0, 0.1, -0.04]} material={m.wall}>
        <planeGeometry args={[w + 1.8, h + 1.5]} />
      </mesh>
      <mesh position={[0, (top + h / 2 + 0.06) / 2, 0.03]} material={m.beam}>
        <planeGeometry args={[w + 0.4, top - h / 2 - 0.06]} />
      </mesh>
      <mesh position={[0, top + 0.08, 0.06]} material={m.body} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.12, 6]} />
      </mesh>
      <mesh position={[0, top, 0.12]} material={m.body} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.024, 0.024, Math.min(0.6, w * 0.4), 12]} />
      </mesh>
      <mesh position={[0, top - 0.034, 0.12]} material={m.lens}>
        <boxGeometry args={[Math.min(0.56, w * 0.38), 0.006, 0.03]} />
      </mesh>
    </group>
  );
}
